import { giphyBlack, giphyRed } from '@giphy/colors'
import { useContext, useRef, useState } from 'react'
import DotsLoader from 'shared/components/loaders/dots'
import UserContext from 'shared/contexts/user'
import SuperCutSegmentationType from 'site/creation-tools/types/super-cut-segmentation-type'
import styled from 'styled-components'
import { CreationToolSourceFile } from '../../../types'
import CreationToolError from '../../../types/error'
import CreationToolRating from '../../../types/rating'
import CreationToolWatermarkPosition from '../../../types/watermark'
import AlertModal from '../../alert-modal'
import { ColumnBlock, ContainerBlock, SplitBlock } from '../../blocks'
import Fields from '../shared/components/capture-info'
import CapturePreview from '../shared/components/capture-preview'
import { ICapture, ICustomFont, INewCaptureInfo, IWatermark } from '../shared/types'
import API from '../shared/utils/api'
import BaseModal from './base'

const api = new API()

const LoaderContainer = styled.div`
    width: 100%;
    align-items: center;
    display: flex;
    justify-content: center;
`

const Container = styled(ContainerBlock)`
    position: relative;
    width: 1040px;
`

const Wrapper = styled(SplitBlock)`
    background: ${giphyBlack};
    border-top-left-radius: 0;
    border-top-right-radius: 0;
`

const Preview = styled(CapturePreview)`
    padding-top: 30px;
`

type Props = {
    capture: ICapture
    preview: string
    updateCapture: (capture: ICapture) => void
    onClose: () => void
}

const CreationToolsEditCaptureModal = ({ capture, updateCapture, preview, onClose }: Props) => {
    const { user } = useContext(UserContext)
    const [error, setError] = useState<CreationToolError>()
    const [hasChanges, setHasChanges] = useState<boolean>(false)
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false)
    const [isUpdating, setIsUpdating] = useState<boolean>(false)
    const [watermark, setWatermark] = useState<IWatermark | undefined>(capture.watermark)
    const [customFont, setCustomFont] = useState<ICustomFont | undefined>(capture.custom_font)
    const [watermarkPosition, setWatermarkPosition] = useState<CreationToolWatermarkPosition | undefined>(
        capture.upload_metadata?.watermark_position
    )
    const watermarkWidthRef = useRef<number>()
    const defaultWatermarkWidth = capture.upload_metadata?.watermark_width
        ? parseInt(capture.upload_metadata?.watermark_width) || undefined
        : undefined

    const onWatermarkResize = (width: number) => {
        watermarkWidthRef.current = width
    }

    const onUpdate = (hasChanges: boolean, info: INewCaptureInfo) => {
        setHasChanges(hasChanges)
        setWatermark(info.watermark)
        setCustomFont(info.font)
        setWatermarkPosition(info.watermarkPosition as CreationToolWatermarkPosition)
    }

    const onSave = async (info: INewCaptureInfo) => {
        setIsUpdating(true)

        try {
            const response = await api.saveCaptureEdits(
                { ...info, watermarkWidth: watermarkWidthRef.current },
                capture.id
            )

            if (response.status === 'error') {
                throw 'Unable to update capture'
            }
            updateCapture(response.capture)
        } catch (err) {
            setIsUpdating(false)
            setError(CreationToolError.SuperCutUpdateFailed)
        }

        onClose()
    }

    const onCloseClick = () => {
        if (isUpdating) {
            return
        }

        if (hasChanges) {
            setShowConfirmModal(true)
            return
        }

        onClose()
    }

    return (
        <BaseModal
            {...{
                error,
                isLoading: false,
                onClose: onCloseClick,
            }}
        >
            <Container style={showConfirmModal ? { display: 'none' } : {}}>
                {isUpdating ? (
                    <LoaderContainer>
                        <DotsLoader />
                    </LoaderContainer>
                ) : (
                    <>
                        <Wrapper>
                            <ColumnBlock>
                                <Preview
                                    {...{
                                        source: {
                                            hasAudio: false,
                                            width: capture.width,
                                            height: capture.height,
                                            source: preview,
                                        } as CreationToolSourceFile,
                                        width: 460,
                                        watermark: watermark?.imageUrl,
                                        watermarkPosition,
                                        defaultWatermarkWidth,
                                        onWatermarkResize,
                                        onCaptionMove: () => {},
                                        autoCaption: capture.process_type === SuperCutSegmentationType.Caption,
                                        captionStyle: capture.upload_metadata?.caption_style,
                                        customFont: customFont,
                                        onAdvancedTab: false,
                                    }}
                                />
                            </ColumnBlock>
                            <ColumnBlock>
                                <Fields
                                    {...{
                                        isUpdating,
                                        onSave,
                                        captureInfo: {
                                            title: capture.title,
                                            username: user?.username,
                                            sourceUrl: capture.upload_metadata?.source_url,
                                            font: capture.custom_font,
                                            tags: capture.upload_metadata?.tags?.split(',').filter((i) => i).map((tag) => ({text: tag, isFeatured:(capture.upload_metadata?.featured_tags?.split(',') || []).includes(tag)})),
                                            watermark: capture.watermark,
                                            watermarkPosition: capture.upload_metadata?.watermark_position,
                                            rating:
                                                (capture.upload_metadata?.rating as CreationToolRating) ||
                                                CreationToolRating.Unrated,
                                            isPublic: !capture.upload_metadata?.is_private,
                                            description: capture.upload_metadata?.description,
                                            segmentationType: capture.process_type,
                                        },
                                        saveLabel: 'Update Capture',
                                        onUpdate: onUpdate,
                                        onCancel: onCloseClick,
                                        isEditMode: true,
                                        onSectionChange: () => {},
                                    }}
                                />
                            </ColumnBlock>
                        </Wrapper>
                    </>
                )}
            </Container>

            {showConfirmModal && (
                <AlertModal
                    {...{
                        stickerId: 'jOsoGmmWGSloPU8fMH',
                        title: 'You will lose your changes to this capture!',
                        primaryButtonLabel: 'Discard Changes',
                        primaryButtonColor: giphyRed,
                        secondaryButtonLabel: 'Nevermind',
                        onPrimaryButtonClick: onClose,
                        onSecondaryButtonClick: () => setShowConfirmModal(false),
                    }}
                />
            )}
        </BaseModal>
    )
}

export default CreationToolsEditCaptureModal
